import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import TreatmentSummaryDisplay from './TreatmentSummaryDisplay';
import ClipboardCheckIcon from './icons/ClipboardCheckIcon';
import XIcon from './icons/XIcon';
import ClipboardIcon from './icons/ClipboardIcon';
import CheckIcon from './icons/CheckIcon';

const PlanModal: React.FC<{
    plan: object | null;
    genText: string | null;
    onClose: () => void;
}> = ({ plan, genText, onClose }) => {
    const [activeTab, setActiveTab] = useState<'summary' | 'plan'>(plan ? 'summary' : 'plan');
    const [isCopied, setIsCopied] = useState(false);

    const copyToClipboard = () => {
        const textToCopy = JSON.stringify(plan, null, 2);
        navigator.clipboard.writeText(textToCopy).then(() => {
            setIsCopied(true);
            setTimeout(() => setIsCopied(false), 2000);
        });
    };

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [onClose]);

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-md"
            onClick={onClose}
        >
            <motion.div
                initial={{ scale: 0.95, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.95, y: 20, opacity: 0 }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
                className="relative w-full max-w-2xl h-[80vh] max-h-[700px] bg-zinc-950/70 backdrop-blur-2xl rounded-2xl border border-white/10 shadow-2xl shadow-black/40 flex flex-col overflow-hidden"
                onClick={e => e.stopPropagation()}
            >
                <div className="flex-shrink-0 p-5 flex justify-between items-center border-b border-white/10">
                    <h2 className="text-xl font-bold text-zinc-100 flex items-center gap-3">
                        <ClipboardCheckIcon className="w-6 h-6 text-teal-400" />
                        AI Treatment Plan
                    </h2>
                    <button onClick={onClose} className="p-2 text-zinc-400 rounded-full hover:bg-white/10 transition-colors"><XIcon className="w-5 h-5" /></button>
                </div>

                <div className="flex-shrink-0 px-6 border-b border-white/10">
                    <nav className="-mb-px flex space-x-6" aria-label="Tabs">
                        {plan && (
                            <button onClick={() => setActiveTab('summary')} className={`relative py-4 px-1 text-sm font-semibold transition-colors ${activeTab === 'summary' ? 'text-teal-400' : 'text-zinc-400 hover:text-zinc-200'}`}>
                                Summary
                                {activeTab === 'summary' && <motion.div layoutId="tab-underline-modal" className="absolute bottom-0 left-0 right-0 h-0.5 bg-teal-500" />}
                            </button>
                        )}
                        <button onClick={() => setActiveTab('plan')} className={`relative py-4 px-1 text-sm font-semibold transition-colors ${activeTab === 'plan' ? 'text-teal-400' : 'text-zinc-400 hover:text-zinc-200'}`}>
                            Full Plan (JSON)
                            {activeTab === 'plan' && <motion.div layoutId="tab-underline-modal" className="absolute bottom-0 left-0 right-0 h-0.5 bg-teal-500" />}
                        </button>
                    </nav>
                </div>

                <div className="flex-1 overflow-y-auto p-6">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={activeTab}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.2 }}
                        >
                            {activeTab === 'summary' ? (
                                <TreatmentSummaryDisplay plan={plan} genText={null} />
                            ) : (
                                <div className="relative">
                                    <button onClick={copyToClipboard} className="absolute top-2 right-2 p-1.5 rounded-md bg-zinc-800/80 hover:bg-zinc-700 transition-colors z-10" aria-label="Copy JSON">
                                        {isCopied ? <CheckIcon className="w-4 h-4 text-teal-400" /> : <ClipboardIcon className="w-4 h-4 text-zinc-300" />}
                                    </button>
                                    <pre className="text-xs text-zinc-300 bg-black/30 p-4 rounded-lg overflow-x-auto">
                                        <code>
                                            {JSON.stringify(plan, null, 2)}
                                        </code>
                                    </pre>
                                </div>
                            )}
                        </motion.div>
                    </AnimatePresence>
                </div>
            </motion.div>
        </motion.div>
    );
};

export default PlanModal;
